<template>
  <div
    class="bubble-factor bblfctr"
    :class="[{ bblfctr_loading: loading || error }]"
  >
    <MessageOverlaySkeleton
      v-if="loading || error"
      class="position-absolute z-index_4"
      style="--radius: 4px"
    >
      <div
        v-if="error"
        class="gw_mesblock_container"
        style="--size-title: 1.125rem"
      >
        <div class="gw_mesblock">
          <div
            class="gw_mesblock_title"
            v-html="$t(`messages.oops_error`)"
          />
          <div class="text-small clr-grey d-flex mt-2">
            <div
              class="mr-1"
              v-html="
                isCustomSolution ? solElite && solElite.custom_solution_id : gameformat.gametype
              "
            ></div>
            <div class="uppercase">/ {{ status }}</div>
          </div>
        </div>
      </div>
      <LoaderSpinner
        v-else
        :style="`--size:4rem`"
      />
    </MessageOverlaySkeleton>

    <div class="bblfctr_content">
      <div class="bblfctr_axes">
        <div class="bblfctr_axes_x">
          <div
            v-for="player in players"
            :key="player.position"
            class="bblfctr_axes_cell"
          >
            <div class="bblfctr_axes_content">
              <div class="bblfctr_player_position">
                {{ getPlayerHtmlPosition(player.position) }}
              </div>
              <div class="bblfctr_player_stack">
                {{ player.stack || '-' }}
              </div>
            </div>
          </div>
        </div>
        <div class="bblfctr_axes_y">
          <div
            v-for="player in players"
            :key="player.position"
            class="bblfctr_axes_cell"
          >
            <div class="bblfctr_axes_content">
              <div class="bblfctr_player_position">
                {{ getPlayerHtmlPosition(player.position) }}
              </div>
              <div class="bblfctr_player_stack">
                {{ player.stack || '-' }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bblfctr_grid">
        <div
          v-for="(column, colIdx) in columns"
          :key="colIdx"
          class="bblfctr_grid_rows"
        >
          <div
            v-for="(cell, cIdx) in column.cells"
            :key="`${column.position}_${cIdx}`"
            class="bblfctr_grid_cell"
            :style="!cell.empty ? `--cell-clr-back:var(${cell.color})` : ''"
          >
            <div
              v-if="!cell.empty"
              class="bblfctr_grid_cell_content"
            >
              <div class="bblfctr_grid_cell_content_top">
                <span
                  v-if="cell.risk_premium !== 0"
                  class="text-medium"
                  >{{ cell.risk_premium > 0 ? '+' : '' }}</span
                >{{ numFixedUnit(cell.risk_premium * 100, 'risk_premium') }}%
              </div>
              <div class="bblfctr_grid_cell_content_bottom">
                {{ numFixedUnit(cell.bubble_factor, 'bubble_factor') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MessageOverlaySkeleton from '@/components/Poker/Overlays/MessageOverlaySkeleton'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner'
import format_mix from '@/mixins/format_mix'
import { getPercentageOfMax } from '@/helpers/helpers'
import float_mix from '@/mixins/float_mix'
import { getPlayersDefault } from '@/store/gamestate/gamestate'
import fetch_mix from '@/mixins/fetch_mix'

export default {
  name: 'BubbleFactor',
  components: {
    MessageOverlaySkeleton,
    LoaderSpinner,
  },
  mixins: [format_mix, float_mix, fetch_mix],
  props: {
    gameformat: Object,
    solElite: Object,
  },
  data() {
    return {
      bubble_call: {
        conf: {
          method: 'get',
          endpoint: '/bubble-factors/',
          apiVersion: 'v4',
        },
        loading: false,
        loaded: false,
        status: 0,
        error: null,
      },
      apiData: null,
    }
  },
  created() {
    this.getData()
  },
  methods: {
    async getData() {
      if (this.bubble_call.loading) return

      this.bubble_call.conf.params = this.isCustomSolution
        ? this.getCustomSolutionParams()
        : {
            gametype: this.gameformat.gametype,
            depth: this.gameformat.depth,
            stacks: this.gameformat.stacks,
          }

      const { data, error } = await this.fch_getData(this.bubble_call)
      if (this.bubble_call.canceled) return
      if (error) return
      this.apiData = data
    },
    getCustomSolutionParams() {
      const params = {}

      if (this.solElite?.custom_solution_id) {
        params.custom_solution_id = this.solElite.custom_solution_id
      }

      if (this.solElite?.custom_tree_id) {
        params.custom_tree_id = this.solElite.custom_tree_id
      }

      return params
    },
  },
  computed: {
    isCustomSolution() {
      return this.solElite?.want_use && this.solElite.tournamentType === 'icm'
    },
    loading() {
      return this.bubble_call.loading
    },
    error() {
      return this.bubble_call.error
    },
    status() {
      return this.bubble_call.status
    },
    skeletonData() {
      const players = getPlayersDefault({
        customTree: this.solElite?.is_using && this.solElite.cTree,
        gameformat: this.gameformat,
      })
      const res = players.map(p => {
        const r = {
          position: p.position,
          cells: players.map(pp => ({
            empty: true,
          })),
        }
        return r
      })
      return res
    },
    columns() {
      if (this.loading || this.error) {
        return this.skeletonData
      }

      const bubbleFactors = this.apiData?.bubble_factors || []

      const min = -0.04
      const max = 0.15
      const minToZero = Math.abs(min)
      const maxToOne = max + minToZero

      // 0 red = 0.15 risk_premium
      // 0.5 yellow = 0.05 risk_premium
      // 1 green = -0.04 risk_premium

      const res = bubbleFactors.map(i => {
        const cells = i.versus_positions.map(cc => {
          const value = cc.risk_premium
          let color = value <= min ? `--clr-eveqeqrbl100` : value >= max ? `--clr-eveqeqrbl-10` : ''
          if (!color) {
            const zeroedValue = value + minToZero
            let val = getPercentageOfMax({
              max: maxToOne,
              value: zeroedValue,
            })
            // reverse
            val = Math.abs(1 - val)

            const gradient = Math.round((val * 100) / 5) * 5
            color = `--clr-eveqeqrbl${gradient}`
          }
          const rr = {
            position: cc.position,
            bubble_factor: cc.bubble_factor,
            risk_premium: cc.risk_premium,
            empty: cc.bubble_factor === -1,
            color,
          }
          return rr
        })
        const r = {
          position: i.position,
          cells,
        }
        return r
      })
      return res
    },
    players() {
      if (this.loading || this.error) {
        return this.skeletonData
      }
      const bubbleFactors = this.apiData?.bubble_factors || []
      const players = this.apiData?.players
      const res = bubbleFactors.map(i => {
        const player = players.find(p => p.position === i.position)
        const r = {
          position: i.position,
          stack: this.numFixedUnit(Number(player.stack), 'bb'),
        }
        return r
      })
      return res
    },
  },
}
</script>

<style lang="scss" scoped>
.bblfctr {
  --content-opacity: 1;
  --bubble-clr-back: var(--clr-black-1);
  --cell-clr-back: var(--clr-black-1);
  --cell-dimensions: 3.75rem;

  --axes-cell-clr-back: var(--clr-black-2);
  --axes-cell-top-fnt-size: 0.875rem;
  --axes-cell-top-weight: 600;
  --axes-cell-bottom-fnt-size: 0.8rem;
  --axes-cell-bottom-weight: 500;

  --cell-top-fnt-size: 0.875rem;
  --cell-bottom-fnt-size: 0.8rem;
  --cell-top-weight: 600;
  --cell-bottom-weight: 600;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  &_content {
    background: var(--bubble-clr-back);
    position: relative;
    padding: var(--cell-dimensions) 0 0 var(--cell-dimensions);
    text-shadow: var(--range-cell-text-shadow);
    color: var(--clr-range_text);
    overflow: hidden;
    border-radius: 4px;
    transition: 0.22s;
  }
  --player-name-size: 0.875rem;
  --clr-player-stack: var(--clr-grey-3);
  &_player {
    &_position {
      font-weight: 700;
      font-size: var(--player-name-size);
      color: var(--clr-white-0);
      line-height: 1.3;
    }
    &_stack {
      line-height: 1.2;
      font-weight: 700;
      font-size: var(--player-name-size);
      color: var(--clr-player-stack);
    }
  }
  &_axes {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    &_x {
      display: flex;
      gap: 1px;
      position: relative;
      left: var(--cell-dimensions);
      .bblfctr_axes_cell:nth-child(1) {
        border-radius: 4px 0px 0px 0px;
      }
    }
    &_y {
      display: flex;
      flex-direction: column;
      gap: 1px;
      .bblfctr_axes_cell:nth-child(1) {
        border-radius: 4px 0px 0px 0px;
      }
    }
    &_cell {
      height: var(--cell-dimensions);
      width: var(--cell-dimensions);
      font-size: var(--axes-cell-fnt-size);
      font-weight: var(--axes-cell-weight);
      background: var(--axes-cell-clr-back);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      &_content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        &_top {
          color: var(--clr-white-0);
          font-size: var(--axes-cell-top-fnt-size);
          font-weight: var(--axes-cell-top-weight);
        }
        &_bottom {
          color: var(--clr-white-0);
          font-size: var(--axes-cell-bottom-fnt-size);
          font-weight: var(--axes-cell-bottom-weight);
        }
      }
    }
  }
  &_grid {
    background: var(--bubble-clr-back);
    display: flex;
    gap: 1px;
    flex-direction: column;
    &_rows {
      display: flex;
      gap: 1px;
      &:nth-last-child(2) {
        .bblfctr_grid_cell:nth-last-child(1) {
          border-radius: 0px 0px 4px 0px;
        }
      }
      &:nth-last-child(1) {
        .bblfctr_grid_cell:nth-last-child(2) {
          border-radius: 0px 0px 4px 0px;
        }
      }
    }
    &_cell {
      background: var(--cell-clr-back);
      height: var(--cell-dimensions);
      width: var(--cell-dimensions);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      &_content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        &_top {
          font-size: var(--cell-top-fnt-size);
          font-weight: var(--cell-top-weight);
        }
        &_bottom {
          font-size: var(--cell-bottom-fnt-size);
          font-weight: var(--cell-bottom-weight);
        }
      }
    }
  }
}
</style>
